import { useMemo } from 'react';
import { haptic } from '../lib/telegram';

/*
  Небольшие SVG-графики вместо тяжёлой библиотеки: полный контроль над темой
  Telegram и никакого лишнего веса в бандле мини-аппа.
*/

export interface TrendPoint {
  readonly date: string;
  readonly value: number;
}

/** Линия тренда с заливкой. Сглаживания нет намеренно — данные дневные, «горбы» врут. */
export function TrendChart({
  points,
  height = 140,
  color = 'var(--color-accent)',
  formatValue = (value: number) => String(value),
}: {
  points: readonly TrendPoint[];
  height?: number;
  color?: string;
  formatValue?: (value: number) => string;
}) {
  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const width = 320;
    const padding = { top: 8, right: 4, bottom: 18, left: 4 };
    const innerWidth = width - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom;

    const values = points.map((point) => point.value);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;

    const step = points.length > 1 ? innerWidth / (points.length - 1) : 0;
    const coords = points.map((point, index) => ({
      x: padding.left + index * step,
      y: padding.top + innerHeight - ((point.value - min) / span) * innerHeight,
    }));

    const first = coords[0]!;
    const last = coords[coords.length - 1]!;
    const baseline = padding.top + innerHeight;

    const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
    const area = `${line} L${last.x.toFixed(1)},${baseline.toFixed(1)} L${first.x.toFixed(1)},${baseline.toFixed(1)} Z`;

    return { width, line, area, max, min, coords };
  }, [points, height]);

  if (!geometry) {
    return <p className="py-8 text-center t-footnote text-[var(--color-hint)]">Нет данных за период</p>;
  }

  const gradientId = `trend-${color.replace(/[^a-z]/gi, '')}`;

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${geometry.width} ${height}`}
        className="w-full"
        role="img"
        aria-label={`График: от ${formatValue(geometry.min)} до ${formatValue(geometry.max)}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={geometry.area} fill={`url(#${gradientId})`} />
        <path
          d={geometry.line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {geometry.coords.length <= 32 &&
          geometry.coords.map((coord, index) => (
            <circle key={index} cx={coord.x} cy={coord.y} r="2" fill={color} />
          ))}
      </svg>
      <figcaption className="flex justify-between px-1 t-caption text-[var(--color-hint)]">
        <span>{points[0]?.date.slice(5)}</span>
        <span>{points[points.length - 1]?.date.slice(5)}</span>
      </figcaption>
    </figure>
  );
}

/** Горизонтальное распределение оценок 5→1. */
export function RatingBars({
  data,
  onSelect,
}: {
  data: ReadonlyArray<{ rating: number; count: number }>;
  onSelect?: (rating: number) => void;
}) {
  const max = Math.max(1, ...data.map((item) => item.count));
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-1.5">
      {data.map((item) => (
        <button
          key={item.rating}
          className={`flex w-full items-center gap-2 ${onSelect ? 'pressable' : 'cursor-default'}`}
          disabled={!onSelect || item.count === 0}
          onClick={() => {
            haptic.select();
            onSelect?.(item.rating);
          }}
        >
          <span className="w-6 text-right t-footnote text-[var(--color-hint)]">{item.rating}★</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-surface)]">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${(item.count / max) * 100}%`,
                background:
                  item.rating >= 4
                    ? 'var(--color-positive)'
                    : item.rating === 3
                      ? 'var(--color-neutral)'
                      : 'var(--color-negative)',
              }}
            />
          </div>
          <span className="w-12 text-right t-footnote tabular-nums text-[var(--color-hint)]">
            {total > 0 ? `${Math.round((item.count / total) * 100)}%` : '0%'}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Кольцевая диаграмма тональности. */
export function SentimentDonut({
  data,
  onSelect,
}: {
  data: ReadonlyArray<{ sentiment: string; count: number }>;
  onSelect?: (sentiment: string) => void;
}) {
  const colors: Record<string, string> = {
    POSITIVE: 'var(--color-positive)',
    NEUTRAL: 'var(--color-neutral)',
    NEGATIVE: 'var(--color-negative)',
  };
  const labels: Record<string, string> = {
    POSITIVE: 'Позитив',
    NEUTRAL: 'Нейтрально',
    NEGATIVE: 'Негатив',
  };

  const total = data.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) {
    return <p className="py-6 text-center t-footnote text-[var(--color-hint)]">Нет данных</p>;
  }

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center gap-5">
      <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90" role="img" aria-label="Тональность отзывов">
        {data.map((item) => {
          const fraction = item.count / total;
          const dash = fraction * circumference;
          const element = (
            <circle
              key={item.sentiment}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={colors[item.sentiment]}
              strokeWidth="14"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return element;
        })}
      </svg>
      <ul className="flex-1 space-y-1.5">
        {data.map((item) => (
          <li key={item.sentiment}>
            <button
              className={`flex w-full items-center gap-2 t-subhead ${onSelect ? 'pressable' : 'cursor-default'}`}
              disabled={!onSelect || item.count === 0}
              onClick={() => {
                haptic.select();
                onSelect?.(item.sentiment);
              }}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: colors[item.sentiment] }}
                aria-hidden="true"
              />
              <span className="flex-1 text-left">{labels[item.sentiment]}</span>
              <span className="tabular-nums text-[var(--color-hint)]">
                {Math.round((item.count / total) * 100)}%
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Темы отзывов горизонтальными полосами.
 *
 * Полоса показывает, насколько тема заметна, а цвет — насколько она болезненна.
 * Строка кликабельна: цифра без возможности прочитать отзывы за ней не даёт
 * понять, что именно не так с «обслуживанием».
 */
export function TopicBars({
  data,
  tone,
  onSelect,
}: {
  data: ReadonlyArray<{ topic: string; count: number; avgRating: number }>;
  tone: string;
  onSelect: (topic: string) => void;
}) {
  const max = Math.max(...data.map((item) => item.count), 1);

  return (
    <ul className="space-y-2">
      {data.map((item) => (
        <li key={item.topic}>
          <button
            className="pressable block w-full text-left"
            onClick={() => {
              haptic.select();
              onSelect(item.topic);
            }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 flex-1 truncate t-callout first-letter:uppercase">{item.topic}</span>
              <span className="shrink-0 t-footnote tabular-nums text-[var(--color-hint)]">
                {item.count} · {item.avgRating.toFixed(1)}★
              </span>
            </div>
            <div className="mt-1.5 h-[6px] overflow-hidden rounded-full bg-[var(--color-surface)]">
              <div
                className="h-full rounded-full transition-[width] duration-500 [transition-timing-function:var(--ease-out)]"
                style={{ width: `${Math.round((item.count / max) * 100)}%`, background: tone }}
              />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
